/**
 * Store Preferences Hook
 * Manages store preferences state and API interactions
 */

import { useState, useCallback, useEffect } from 'react';
import { api } from '../../../../services/api';
import type { UseStorePreferencesReturn, StorePreferences, StoreData } from '../types';
import { STORE_PREFS_DEFAULTS } from '../utils/constants';
import { isValidZipcode } from '../utils/helpers';

export function useStorePreferences(): UseStorePreferencesReturn {
  // State
  const [preferences, setPreferences] = useState<StorePreferences | null>(null);
  const [nearbyStores, setNearbyStores] = useState<StoreData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Local form state (before saving)
  const [localZipcode, setLocalZipcode] = useState('');
  const [localMaxDistance, setLocalMaxDistance] = useState<number>(STORE_PREFS_DEFAULTS.MAX_DISTANCE_DEFAULT);
  const [localBulkThreshold, setLocalBulkThreshold] = useState<number>(STORE_PREFS_DEFAULTS.BULK_THRESHOLD_DEFAULT);

  // Load preferences on mount
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await api.getStorePreferences();
        if (response.success && response.data) {
          const prefs: StorePreferences = {
            zipcode: response.data.zipcode,
            home_store_id: response.data.home_store_id,
            max_distance_miles: response.data.max_distance_miles,
            road_trip_stores: response.data.road_trip_stores,
            bulk_threshold: response.data.bulk_threshold,
            home_store: response.data.home_store,
          };
          setPreferences(prefs);

          // Sync local state with loaded preferences
          if (prefs.zipcode) {
            setLocalZipcode(prefs.zipcode);
          }
          setLocalMaxDistance(prefs.max_distance_miles);
          setLocalBulkThreshold(prefs.bulk_threshold);

          // If user has a zipcode, also fetch nearby stores
          if (prefs.zipcode) {
            const nearbyResponse = await api.getNearbyStores(prefs.zipcode, prefs.max_distance_miles);
            if (nearbyResponse.success && nearbyResponse.data) {
              setNearbyStores(nearbyResponse.data.road_trip_stores || []);
            }
          }
        }
      } catch (err) {
        console.error('Failed to load store preferences:', err);
        setError('Failed to load store preferences');
      } finally {
        setIsLoading(false);
      }
    };

    loadPreferences();
  }, []);

  // Find stores by zipcode
  const findStores = useCallback(async (zipcode: string) => {
    if (!isValidZipcode(zipcode)) {
      setError('Please enter a valid 5-digit zipcode');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const response = await api.getNearbyStores(zipcode, localMaxDistance);

      if (response.success && response.data) {
        setNearbyStores(response.data.road_trip_stores || []);

        // Update preferences with new zipcode and auto-selected home store
        const updateResponse = await api.updateStorePreferences({
          zipcode,
          home_store_id: response.data.home_store?.id,
          max_distance_miles: localMaxDistance,
          bulk_threshold: localBulkThreshold,
        });

        if (updateResponse.success && updateResponse.data) {
          setPreferences({
            zipcode: updateResponse.data.zipcode,
            home_store_id: updateResponse.data.home_store_id,
            max_distance_miles: updateResponse.data.max_distance_miles,
            road_trip_stores: updateResponse.data.road_trip_stores,
            bulk_threshold: updateResponse.data.bulk_threshold,
            home_store: updateResponse.data.home_store,
          });
        }
      }
    } catch (err) {
      console.error('Failed to find stores:', err);
      setError('Failed to find stores near that zipcode');
    } finally {
      setIsLoading(false);
    }
  }, [localMaxDistance, localBulkThreshold]);

  // Set home store
  const setHomeStore = useCallback(async (storeId: string) => {
    if (!preferences?.zipcode) {
      setError('Please enter a zipcode first');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      const response = await api.updateStorePreferences({
        zipcode: preferences.zipcode,
        home_store_id: storeId,
        max_distance_miles: localMaxDistance,
        bulk_threshold: localBulkThreshold,
      });

      if (response.success && response.data) {
        setPreferences({
          zipcode: response.data.zipcode,
          home_store_id: response.data.home_store_id,
          max_distance_miles: response.data.max_distance_miles,
          road_trip_stores: response.data.road_trip_stores,
          bulk_threshold: response.data.bulk_threshold,
          home_store: response.data.home_store,
        });
      }
    } catch (err) {
      console.error('Failed to set home store:', err);
      setError('Failed to set home store');
    } finally {
      setIsSaving(false);
    }
  }, [preferences?.zipcode, localMaxDistance, localBulkThreshold]);

  // Update preferences (partial)
  const updatePreferences = useCallback(async (prefs: Partial<{
    zipcode?: string;
    home_store_id?: string;
    max_distance_miles?: number;
    bulk_threshold?: number;
  }>) => {
    if (!preferences?.zipcode && !prefs.zipcode) {
      setError('Please enter a zipcode first');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      const response = await api.updateStorePreferences({
        zipcode: prefs.zipcode ?? preferences?.zipcode ?? undefined,
        home_store_id: prefs.home_store_id ?? preferences?.home_store_id ?? undefined,
        max_distance_miles: prefs.max_distance_miles ?? localMaxDistance,
        bulk_threshold: prefs.bulk_threshold ?? localBulkThreshold,
      });

      if (response.success && response.data) {
        setPreferences({
          zipcode: response.data.zipcode,
          home_store_id: response.data.home_store_id,
          max_distance_miles: response.data.max_distance_miles,
          road_trip_stores: response.data.road_trip_stores,
          bulk_threshold: response.data.bulk_threshold,
          home_store: response.data.home_store,
        });

        // Refresh nearby stores if distance changed
        if (prefs.max_distance_miles && response.data.zipcode) {
          const nearbyResponse = await api.getNearbyStores(
            response.data.zipcode,
            prefs.max_distance_miles
          );
          if (nearbyResponse.success && nearbyResponse.data) {
            setNearbyStores(nearbyResponse.data.road_trip_stores || []);
          }
        }
      }
    } catch (err) {
      console.error('Failed to update preferences:', err);
      setError('Failed to update preferences');
    } finally {
      setIsSaving(false);
    }
  }, [preferences, localMaxDistance, localBulkThreshold]);

  // Save current local state as preferences
  const savePreferences = useCallback(async () => {
    if (!preferences?.zipcode) {
      setError('Please enter a zipcode first');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      const response = await api.updateStorePreferences({
        zipcode: preferences.zipcode,
        home_store_id: preferences.home_store_id ?? undefined,
        max_distance_miles: localMaxDistance,
        bulk_threshold: localBulkThreshold,
      });

      if (response.success && response.data) {
        setPreferences({
          zipcode: response.data.zipcode,
          home_store_id: response.data.home_store_id,
          max_distance_miles: response.data.max_distance_miles,
          road_trip_stores: response.data.road_trip_stores,
          bulk_threshold: response.data.bulk_threshold,
          home_store: response.data.home_store,
        });

        // Refresh nearby stores
        const nearbyResponse = await api.getNearbyStores(
          response.data.zipcode!,
          localMaxDistance
        );
        if (nearbyResponse.success && nearbyResponse.data) {
          setNearbyStores(nearbyResponse.data.road_trip_stores || []);
        }
      }
    } catch (err) {
      console.error('Failed to save preferences:', err);
      setError('Failed to save preferences');
    } finally {
      setIsSaving(false);
    }
  }, [preferences, localMaxDistance, localBulkThreshold]);

  return {
    preferences,
    nearbyStores,
    isLoading,
    isSaving,
    error,
    findStores,
    setHomeStore,
    updatePreferences,
    savePreferences,
    localZipcode,
    setLocalZipcode,
    localMaxDistance,
    setLocalMaxDistance,
    localBulkThreshold,
    setLocalBulkThreshold,
  };
}
